/**
 * Category for the graphic system (docs/UX-REDESIGN.md §3).
 *
 * `EventSummary` has no category field. The web app derives one stable id
 * from title, teaser, source name, and audience tags. The same id picks the
 * gradient. Order:
 * 1. Audience tag `uw_only`, or a source named University of Washington → campus.
 * 2. Title or teaser contains meetup, happy hour, networking, or launch → meetup.
 * 3. Title or teaser contains volunteer, community, neighborhood, or library → community.
 * 4. Else → gathering.
 *
 * Words match on boundaries so "launches" is not "launch". The gradient is
 * decorative: the category name is always real text beside the art.
 */

export type EventCategory = 'meetup' | 'campus' | 'community' | 'gathering';

export const CATEGORY_LABEL: Record<EventCategory, string> = {
  meetup: 'Meetup',
  campus: 'Campus',
  community: 'Community',
  gathering: 'Gathering',
};

/** Decorative mark. Never the only carrier of the category name. */
export const CATEGORY_MARK: Record<EventCategory, string> = {
  meetup: '🤝',
  campus: '🌲',
  community: '🌿',
  gathering: '✦',
};

export interface CategorySource {
  title: string;
  teaser?: string | null;
  sourceNames?: readonly string[];
  primarySourceName?: string | null;
  audienceTags?: readonly { code: string }[];
}

const MEETUP = /\b(?:meetup|happy hour|networking|launch)\b/i;
const COMMUNITY = /\b(?:volunteer|community|neighborhood|library)\b/i;

export function deriveCategory(source: CategorySource): EventCategory {
  if (isCampus(source)) return 'campus';
  const text = `${source.title}\n${source.teaser ?? ''}`;
  if (MEETUP.test(text)) return 'meetup';
  if (COMMUNITY.test(text)) return 'community';
  return 'gathering';
}

function isCampus(source: CategorySource): boolean {
  if (source.audienceTags?.some((tag) => tag.code === 'uw_only')) return true;
  const names = [source.primarySourceName, ...(source.sourceNames ?? [])];
  return names.some((name) => typeof name === 'string' && /university of washington/i.test(name));
}
